import { Card, Heading, Link } from "@george-labs.com/design-system"
import {
  LoanCaseDTO,
  ProductInstanceDTO,
  RefinancedLoanDTO,
} from "@smartx/mikro-vt-client-api"
import { Fragment, useCallback, useContext } from "react"
import { useTranslation } from "react-i18next"
import CollapsibleComponent from "../../../components/Collapsible/Collapsible"
import { IField } from "../../../components/DataList/DataList"
import {
  getLoanPurposeBiancoCvtList,
  getProductAccordionTitle,
  productCodes,
} from "../../../functions/caseHelper"
import { DataListComponent } from "../../../Shared"
import OverviewRefinancedLoan from "../OverviewRefinancedLoan/OverviewRefinancedLoan"
import {
  isEhProduct,
  isFhProduct,
  isPal,
  isSzk,
} from "../../../functions/helpers"
import { VALUE_LIST_ENUM } from "../../../functions/setCodeTableValues"
import ConfigContext from "../../../providers/ConfigProvider"

interface OverviewProductProps {
  loanCaseData: LoanCaseDTO
}

export const REFINANCED_TYPES = [
  "HITEL_KIVALTAS",
  "FH_AZONOS_MEGUJ",
  "FH_EMELT_MEGUJ",
  "FH_CSOKKENTETT_MEGUJ",
  "FH_KERETEMELES",
  "FH_HITELKIV",
  "EH_AZONOS_MEGUJ",
  "EH_EMELT_MEGUJ",
  "EH_CSOKKENTETT_MEGUJ",
  "EH_EMELES",
  "EH_HITELKIV",
]

const OverviewProduct: React.FC<OverviewProductProps> = ({ loanCaseData }) => {
  const configContext = useContext(ConfigContext)
  const { t } = useTranslation()

  const getRefinancedLoans = (
    productInstance: ProductInstanceDTO,
  ): RefinancedLoanDTO[] => {
    if (
      !productInstance ||
      !REFINANCED_TYPES.includes(productInstance.loanPurpose)
    ) {
      return null
    }

    return productInstance.refinancedLoans
  }

  const getInterestField = (product: ProductInstanceDTO): IField | null => {
    if (isSzk(product.productCode)) {
      return null
    }

    const label = t("case.fields.productInstance.loanInterestMerged")
    const appendText = `% / ${t("common.year")}`

    if (!product.loanInterestBase || product.loanInterestBase === "UNUSED") {
      return {
        label,
        value: product.loanInterest,
        fieldType: "decimal",
        appendText,
      }
    }

    const field: IField = {
      label,
      value: product.loanInterestBase,
      fieldType: "CVT",
      list: VALUE_LIST_ENUM.InterestBaseListBiancoParam,
      noWhiteSpaceAppendText:
        (product.loanInterestBase === "FIX" ? ": " : " + ") +
        (product.loanInterest ?? 0) +
        appendText,
    }

    if (["1HAVI_BUBOR", "3HAVI_BUBOR"].includes(product.loanInterestBase)) {
      field.info = (
        <>
          {t("case.fields.productInstance.loanInterestMerged.BUBOR.tooltip")}{" "}
          <Link
            href={configContext?.interestBuborUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {configContext?.interestBuborUrlText}
          </Link>
        </>
      )
    } else if (
      ["1HAVI_EURIBOR", "3HAVI_EURIBOR"].includes(product.loanInterestBase)
    ) {
      field.info = (
        <>
          {t("case.fields.productInstance.loanInterestMerged.EURIBOR.tooltip")}
        </>
      )
    }

    return field
  }

  const getFieldListByProduct = (product: ProductInstanceDTO): IField[] => {
    const isEh: boolean = isEhProduct(product)
    const isFh: boolean = isFhProduct(product)
    const isBianco: boolean = product.productCode === productCodes.BIANCO

    const fieldArr: IField[] = [
      isBianco
        ? {
            label:
              product.biancoProduct === "BANKGARANCIA"
                ? t("case.fields.loanPurpose.BANKGARANCIA")
                : t("case.fields.loanPurpose"),
            value: product.loanPurpose,
            fieldType: "MultiCVT",
            lists: getLoanPurposeBiancoCvtList(product),
          }
        : {
            label: t("case.fields.loanPurpose"),
            value: product.loanPurpose,
            fieldType: "CVT",
            list: isEh
              ? "MIKRO_LOAN_PURPOSE_LIST_EH"
              : "MIKRO_LOAN_PURPOSE_LIST_FH",
          },

      getInterestField(product),

      isSzk(product.productCode) && product.productCode !== "ASZKFH"
        ? {
            label: t("case.fields.productInstance.loanInterest"),
            value: product.loanInterest,
            fieldType: "decimal",
            appendText: `% / ${t("common.year")}`,
          }
        : null,

      {
        label: t("case.fields.productInstance.handlingCharges"),
        value: product.handlingCharges,
        fieldType: "decimal",
        appendText: `%${t("common.per.month")}`,
      },
      isFh
        ? {
            label: t("case.fields.productInstance.availabilityFee"),
            value: product.availabilityFee,
            fieldType: "decimal",
            appendText: `% / ${t("common.year")}`,
          }
        : null,
      {
        label: t("case.fields.productInstance.contractFeePercent"),
        value: product.contractFeePercent,
        fieldType: "decimal",
        appendText: " %",
      },
      {
        label: t("case.fields.productInstance.contractFeeMin"),
        value: product.contractFeeMin,
        fieldType: "number",
      },
      {
        label: t("case.fields.productInstance.contractFeeMax"),
        value: product.contractFeeMax,
        fieldType: "number",
      },
      isFh
        ? {
            label: t("case.fields.productInstance.annualRevisionFeePercent"),
            value: product.annualRevisionFeePercent,
            fieldType: "decimal",
            appendText: " %",
          }
        : null,
      isFh
        ? {
            label: t("case.fields.productInstance.annualRevisionFeeMin"),
            value: product.annualRevisionFeeMin,
            fieldType: "number",
          }
        : null,
      isFh
        ? {
            label: t("case.fields.productInstance.annualRevisionFeeMax"),
            value: product.annualRevisionFeeMax,
            fieldType: "number",
          }
        : null,
      {
        label: t("case.fields.productInstance.contractModificationFeePercent"),
        value: product.contractModificationFeePercent,
        fieldType: "decimal",
        appendText: " %",
      },
      {
        label: t("case.fields.productInstance.contractModificationFeeMin"),
        value: product.contractModificationFeeMin,
        fieldType: "number",
      },
      {
        label: t("case.fields.productInstance.contractModificationFeeMax"),
        value: product.contractModificationFeeMax,
        fieldType: "number",
      },
      {
        label: t("case.fields.productInstance.guaranteeIssuer"),
        value: product.guaranteeIssuer,
        fieldType: "CVT",
        list: "MIKRO_GUARANTEER_LIST",
      },
      isPal(product.productCode)
        ? {
            label: t("offers.calculator.fields.garantiqaPerYearNet"),
            fieldType: "decimal",
            appendText: t(
              "offers.calculator.fields.garantiqaPerYearNet.postfix",
            ),
            value: product.loanCalculationData.netGarantiqaFeePercent,
          }
        : null,
    ]

    return fieldArr.filter(Boolean)
  }

  const getProductTitle = useCallback(
    (product: ProductInstanceDTO) => {
      const accordionTitleParts = [
        "case.accordions.productData",
        getProductAccordionTitle(product),
      ]

      return accordionTitleParts
        ?.filter((el) => !!el)
        ?.map((el) => t(el))
        ?.join(" - ")
    },
    [t],
  )

  return (
    <>
      {loanCaseData.productInstances?.map((product: ProductInstanceDTO) => {
        return (
          <Card
            className="p-2"
            key={`overviewProduct-${loanCaseData.caseId}-${product.productCode}`}
          >
            <Heading level={3}>{getProductTitle(product)}</Heading>

            <Fragment
              key={`overviewProductData-${loanCaseData.caseId}-${product.productCode}`}
            >
              <DataListComponent dataList={getFieldListByProduct(product)} />

              {product.refinancedLoans?.length > 0 && (
                <Heading level={3}>{t("case.refinancedLoans")}</Heading>
              )}

              {/*Kiváltandó hitelek*/
              getRefinancedLoans(product)?.map(
                (refinancedLoan: RefinancedLoanDTO) => {
                  return (
                    <CollapsibleComponent
                      key={refinancedLoan.loanId.toString()}
                      titleString={refinancedLoan.loanId}
                      content={
                        <OverviewRefinancedLoan
                          refinancedLoan={refinancedLoan}
                          product={product}
                        ></OverviewRefinancedLoan>
                      }
                    ></CollapsibleComponent>
                  )
                },
              )}
            </Fragment>
          </Card>
        )
      })}
    </>
  )
}

export default OverviewProduct
